import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import launch components (lazy-loaded for performance)
const LaunchDashboard = React.lazy(() => import("./LaunchDashboard"));
const LaunchNew = React.lazy(() => import("./LaunchNew"));
const LaunchDetail = React.lazy(() => import("./LaunchDetail"));

/**
 * Launch Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (launch/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/launch/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const launchModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "launch",
    name: "Launch",
    description: "Create and manage tenant websites with AI-assisted setup and provisioning",
    version: "1.0.0",
    enabled: true,
    category: "platform",
    order: 20,
    icon: "🚀",
    color: "#8B5CF6",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/launch/new",
            component: LaunchNew,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/launch/:id",
            component: LaunchDetail,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/launch",
            component: LaunchDashboard,
            permissionLevel: "view",
            useLayout: true,
        },
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Sites",
            path: "/launch",
            icon: "🚀",
            permissionLevel: "view",
        },
        {
            label: "Launch New",
            path: "/launch/new",
            icon: "➕",
            permissionLevel: "create",
        },
    ],

    // Permissions come from the backend (single source of truth).
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Launch New Business",
            path: "/launch/new",
            icon: "🚀",
            description: "Create and launch a new tenant website",
        },
        {
            label: "View Sites",
            path: "/launch",
            icon: "🌐",
            description: "View all launched websites",
        },
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2026-03-19",
            title: "Initial Launch Module",
            features: [
                "Dashboard with site status overview and stat cards",
                "Quick Create flow for fast tenant provisioning",
                "AI-Assisted setup with generate, preview, and refine workflow",
                "Real-time provisioning progress tracking with step-by-step display",
                "AI Brainstorm panel for site spec generation and refinement",
                "Site detail page with provisioning status and management controls",
            ],
            notes: "First release of the Launch module for creating and managing tenant websites with AI assistance.",
        },
    ],

    // ROADMAP
    roadmap: [],
};

export default launchModuleConfig;
